import "react-app-polyfill/stable";

import GitInfo from "react-git-info/macro";

import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import App from "./components/main/App";
import * as serviceWorker from "./serviceWorker";
import * as Sentry from "@sentry/react";
import { applyHacks } from "hacks";
import { createBrowserHistory } from "history";
import { DANGEROUSLY_setTestingToken } from "contexts/user";
import { registerRoot } from "./services/mountControl";

/*
  If a jwt_for_testing query parameter is present in the URL on specific hosts,
   we will use it to set the user token.
  This is useful for testing purposes,
   as it allows us to bypass the normal login flow from the Lexis framework and use a token directly.
*/
function setupJWTInjection(urlParams) {
  const jwt = urlParams.get("jwt_for_testing");
  if (jwt) {
    DANGEROUSLY_setTestingToken(jwt);
  }
}

const { hostname, port, search } = window.location;
const urlParams = new URLSearchParams(search);
const LEX_MACHINA_PORTS = ["3000", "4321"];
const gitInfo = GitInfo();
let mountPath;
if (hostname === "localhost") {
  /*
    If we are not developing on one of Lex Machina's ports, we assume this is Lexis Nexis and mount
    to /litigationanalytics. See issue ticket #35407.
  */
  mountPath = LEX_MACHINA_PORTS.includes(port) ? "/" : "/litigationanalytics";
  setupJWTInjection(urlParams);
} else if (hostname.endsWith(".run.app")) {
  /* This block is for cloud run internal names, like staging sites */
  mountPath = "/";
  setupJWTInjection(urlParams);
} else if (hostname.endsWith("develop.lexmachina.com")) {
  /* new-style access to cloud run via tailnet */
  mountPath = "/";
  setupJWTInjection(urlParams);
} else if (hostname.endsWith("analytics.plus.lexis.com")) {
  mountPath = "/";
  setupJWTInjection(urlParams);
} else if (
  hostname.endsWith(".lexis.com") ||
  hostname.endsWith(".lexisplustesting.com") ||
  hostname.endsWith(".lexisplusforgovernment.com")
) {
  /* Per the routing rules on plus.lexis.com, direct hits with zSelectedDockItem=litigationanalytics or
  newly logged-in users will land on /firsttime and require history state update before proceeding */
  if (window.location.pathname === "/firsttime") {
    window.history.replaceState(
      window.history.state,
      document.title,
      `/litigationanalytics${window.location.search}`,
    );
  }
  mountPath = "/litigationanalytics";
} else {
  throw new Error("Being mounted at an unknown hostname");
}

const history = createBrowserHistory({ basename: mountPath });

if ("development" !== process.env.NODE_ENV) {
  Sentry.init({
    dsn: "https://473796a569da4963b8dc8c03365085db@o988.ingest.sentry.io/5413655",
    integrations: [
      Sentry.browserTracingIntegration({
        routingInstrumentation:
          Sentry.reactRouterV5BrowserTracingIntegration(history),
      }),
    ],
    release: gitInfo.commit.hash,
    // Value between 0 and 1 where 1 is every request, and 0 is none.
    tracesSampleRate: 0.1,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
    ],
  });
}

function ready(callback) {
  // in case the document is already rendered
  if (document.readyState !== "loading") callback();
  // register normal DOMContentLoaded listener if we can
  else if (document.addEventListener)
    document.addEventListener("DOMContentLoaded", callback);
}

ready(function () {
  applyHacks();
  const rootContainer = document.getElementById("root");
  registerRoot(rootContainer);
  ReactDOM.render(
    <React.StrictMode>
      <App mountPath={mountPath} history={history} />
    </React.StrictMode>,
    rootContainer,
  );
});

/**
 * Return the git branch and latest commit information for this release.
 *
 * @returns {string} Git information from this release
 */
window.release = () => {
  const {
    branch,
    commit: { date, hash, message },
  } = gitInfo;
  return `branch: ${branch}\ndate: ${date}\nhash: ${hash}\nmessage: ${message}`;
};

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
